<template>
    <div class="m-profile-page">
      <div class="sticky-header">
        <FeedHeader />
      </div>
  
      <main class="m-profile-content">
        <button class="back-btn" @click="$router.back()">
          ← Назад
        </button>
  
        <div v-if="isLoadingProfile" class="other-skeleton">
          <div class="skeleton title-skeleton"></div>
          <div class="skeleton row-skeleton"></div>
          <div class="skeleton row-skeleton"></div>
          <div class="skeleton button-skeleton"></div>
          <div class="skeleton title-skeleton second"></div>
          <div class="skeleton field-skeleton"></div>
          <div class="skeleton field-skeleton"></div>
        </div>
  
        <div v-else class="profile-other">
          <section class="card">
            <h2>Уведомления</h2>
  
            <label class="checkbox">
              <input type="checkbox" v-model="notifications.buyer" />
              <span>Получать уведомления по email</span>
            </label>
  
            <label class="checkbox">
              <input type="checkbox" v-model="notifications.review" />
              <span>Получать уведомления и подписки через push-уведомления</span>
            </label>
  
            <button
              class="primary-btn"
              :disabled="!isNotificationsChanged || isSavingNotifications"
              @click="saveNotifications"
            >
              <span v-if="isSavingNotifications" class="btn-spinner" aria-hidden="true"></span>
              <span>{{ notificationButtonText }}</span>
            </button>
          </section>
  
          <section class="card disabled">
            <div class="disabled-badge">Скоро</div>
  
            <h2>Сменить пароль</h2>
  
            <div class="field">
              <label>Текущий пароль</label>
              <div class="input-wrapper" :class="{ error: showErrors && !form.current }">
                <input
                  :type="showCurrent ? 'text' : 'password'"
                  v-model.trim="form.current"
                  placeholder="Введите текущий пароль"
                />
                <img src="../../assets/eye-icon.png" class="eye" @click="showCurrent = !showCurrent" />
              </div>
              <p v-if="showErrors && !form.current" class="hint-error">Укажите текущий пароль</p>
            </div>
  
            <div class="field">
              <label>Новый пароль</label>
              <div class="input-wrapper" :class="{ error: showErrors && !validNewLen }">
                <input
                  :type="showNew ? 'text' : 'password'"
                  v-model.trim="form.new"
                  placeholder="Минимум 8 символов"
                />
                <img src="../../assets/eye-icon.png" class="eye" @click="showNew = !showNew" />
              </div>
              <p v-if="showErrors && !validNewLen" class="hint-error">Минимум 8 символов</p>
              <p v-if="showErrors && sameAsCurrent" class="hint-error">Новый пароль не должен совпадать с текущим</p>
            </div>
  
            <div class="field">
              <label>Подтверждение пароля</label>
              <div class="input-wrapper" :class="{ error: showErrors && !matches }">
                <input
                  :type="showConfirm ? 'text' : 'password'"
                  v-model.trim="form.confirm"
                  placeholder="Повторите новый пароль"
                />
                <img src="../../assets/eye-icon.png" class="eye" @click="showConfirm = !showConfirm" />
              </div>
              <p v-if="showErrors && !matches" class="hint-error">Пароли не совпадают</p>
            </div>
  
            <button
              class="primary-btn"
              :disabled="!isPasswordValid || isSavingPassword"
              @click="savePassword"
            >
              <span v-if="isSavingPassword" class="btn-spinner" aria-hidden="true"></span>
              <span>{{ passwordButtonText }}</span>
            </button>
          </section>

          <section class="card theme-card">
            <div class="theme-info">
              <h2>Тема приложения</h2>
              <p>{{ theme === 'dark' ? 'Сейчас включена тёмная тема' : 'Сейчас включена светлая тема' }}</p>
            </div>
            <button class="theme-toggle" @click="toggleTheme">
              <span class="theme-icon">{{ theme === 'dark' ? '🌙' : '☀️' }}</span>
              <span>{{ theme === 'dark' ? 'Тёмная' : 'Светлая' }}</span>
              <div class="toggle-track">
                <div class="toggle-thumb" :class="{ light: theme === 'light' }"></div>
              </div>
            </button>
          </section>

        </div>
      </main>
  
      <transition name="fade">
        <div v-if="showSnackbar" class="snackbar">{{ snackbarMessage }}</div>
      </transition>
  
      <MobileTabBar />
    </div>
  </template>
  
  <script setup>
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import FeedHeader from '../FeedHeader.vue';
  import MobileTabBar from '../MobileTabBar2.vue';
  import UserService from '../../api/UserService';
  
  const theme = ref(document.documentElement.getAttribute('data-theme') || 'light');

  function toggleTheme() {
    const newTheme = theme.value === 'dark' ? 'light' : 'dark';
    theme.value = newTheme;
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  }

  const userId = localStorage.getItem('user_id');
  const user = ref({});
  
  const isLoadingProfile = ref(true);
  const isSavingNotifications = ref(false);
  const isSavingPassword = ref(false);
  const justSavedNotifications = ref(false);
  const justSavedPassword = ref(false);
  
  const showSnackbar = ref(false);
  const snackbarMessage = ref('');
  const snackbarType = ref('success');
  let snackbarTimer = null;
  
  const notifications = ref({
    buyer: true,
    review: true,
  });
  
  const initialNotifications = ref('');
  
  const form = ref({
    current: '',
    new: '',
    confirm: '',
  });
  
  const showCurrent = ref(false);
  const showNew = ref(false);
  const showConfirm = ref(false);
  const showErrors = ref(false);
  
  const isNotificationsChanged = computed(() => {
    return JSON.stringify(notifications.value) !== initialNotifications.value;
  });
  
  const notificationButtonText = computed(() => {
    if (isSavingNotifications.value) return 'Сохраняем…';
    if (justSavedNotifications.value) return 'Готово';
    return 'Сохранить';
  });
  
  const validNewLen = computed(() => form.value.new.length >= 8);
  const matches = computed(() => form.value.new && form.value.new === form.value.confirm);
  const sameAsCurrent = computed(() => form.value.current && form.value.current === form.value.new);
  const isPasswordValid = computed(() => {
    return !!form.value.current && validNewLen.value && matches.value && !sameAsCurrent.value;
  });
  
  const passwordButtonText = computed(() => {
    if (isSavingPassword.value) return 'Сохраняем…';
    if (justSavedPassword.value) return 'Готово';
    return 'Сохранить';
  });
  
  function showSnackbarMessage(message, type = 'success') {
    snackbarMessage.value = message;
    snackbarType.value = type;
    showSnackbar.value = true;
  
    clearTimeout(snackbarTimer);
    snackbarTimer = setTimeout(() => {
      showSnackbar.value = false;
    }, 3000);
  }
  
  function getLocalProfile() {
    try {
      return JSON.parse(localStorage.getItem('user_profile')) || {};
    } catch {
      return {};
    }
  }
  
  function syncNotificationsFromProfile(profile) {
    notifications.value = {
      buyer: profile?.smsMailing ?? true,
      review: profile?.pushMailing ?? true,
    };
  
    initialNotifications.value = JSON.stringify(notifications.value);
  }
  
  function setUser(profile) {
    user.value = profile || {};
    syncNotificationsFromProfile(user.value);
    localStorage.setItem('user_profile', JSON.stringify(user.value));
  }
  
  function updateUserCache(patch) {
    user.value = {
      ...user.value,
      ...patch,
    };
  
    localStorage.setItem('user_profile', JSON.stringify(user.value));
  }
  
  async function loadProfile() {
    const startedAt = performance.now();
    isLoadingProfile.value = true;
  
    const cached = getLocalProfile();
  
    if (cached && Object.keys(cached).length) {
      user.value = cached;
      syncNotificationsFromProfile(cached);
    }
  
    try {
      const fetched = await UserService.getProfile(userId);
      setUser(fetched || {});
    } catch (error) {
      console.error('Ошибка при загрузке настроек:', error);
      showSnackbarMessage('Не удалось загрузить настройки', 'error');
  
      if (!Object.keys(user.value || {}).length) {
        syncNotificationsFromProfile({});
      }
    } finally {
      const minSkeletonMs = 400;
      const elapsed = performance.now() - startedAt;
      const delay = Math.max(0, minSkeletonMs - elapsed);
  
      setTimeout(() => {
        isLoadingProfile.value = false;
      }, delay);
    }
  }
  
  async function saveNotifications() {
    if (!isNotificationsChanged.value || isSavingNotifications.value) return;
  
    isSavingNotifications.value = true;
    justSavedNotifications.value = false;
  
    try {
      await UserService.updateNotifications({
        smsMailing: notifications.value.buyer,
        pushMailing: notifications.value.review,
      });
  
      updateUserCache({
        smsMailing: notifications.value.buyer,
        pushMailing: notifications.value.review,
      });
  
      initialNotifications.value = JSON.stringify(notifications.value);
      justSavedNotifications.value = true;
      showSnackbarMessage('Настройки уведомлений сохранены');
  
      setTimeout(() => {
        justSavedNotifications.value = false;
      }, 1200);
    } catch (error) {
      console.error('❌ Ошибка сохранения уведомлений:', error);
      showSnackbarMessage('Не удалось сохранить уведомления', 'error');
    } finally {
      isSavingNotifications.value = false;
    }
  }
  
  async function savePassword() {
    showErrors.value = true;
  
    if (!isPasswordValid.value || isSavingPassword.value) return;
  
    isSavingPassword.value = true;
    justSavedPassword.value = false;
  
    try {
      await new Promise((resolve) => setTimeout(resolve, 900));
  
      form.value.current = '';
      form.value.new = '';
      form.value.confirm = '';
      showErrors.value = false;
  
      justSavedPassword.value = true;
      showSnackbarMessage('Пароль обновлён');
  
      setTimeout(() => {
        justSavedPassword.value = false;
      }, 1200);
    } catch (error) {
      console.error('❌ Ошибка смены пароля:', error);
      showSnackbarMessage('Не удалось сменить пароль', 'error');
    } finally {
      isSavingPassword.value = false;
    }
  }
  
  onMounted(() => {
    loadProfile();
  });
  
  onBeforeUnmount(() => {
    clearTimeout(snackbarTimer);
  });
  </script>
  
  <style scoped>
  .m-profile-page {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    background: var(--background-color);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .sticky-header {
    flex-shrink: 0;
  }
  
  .m-profile-content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    padding-bottom: 96px;
    box-sizing: border-box;
    -webkit-overflow-scrolling: touch;
  }
  
  .back-btn {
    height: 36px;
    padding: 0 14px;
    margin-bottom: 14px;
    border: none;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
    color: #111;
    font-size: 13px;
    font-family: 'Eastman-Medium', sans-serif;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }
  
  [data-theme='dark'] .back-btn {
    background: rgba(40, 40, 40, 0.92);
    color: #fff;
  }
  
  .profile-other {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }
  
  .card {
    position: relative;
    width: 100%;
    box-sizing: border-box;
    padding: 20px;
    border-radius: 24px;
    background: var(--sidebar-background);
    border: 1px solid #e6e6e6;
    transition: background-color 0.3s, border-color 0.3s;
  }
  
  [data-theme='dark'] .card {
    background: #2f2f2f;
    border-color: #3f3f3f;
  }
  
  .card h2 {
    margin: 0 0 18px;
    font-size: 18px;
    line-height: 1.2;
    font-family: 'Eastman-Bold', sans-serif;
    letter-spacing: -0.4px;
    color: var(--text-primary);
  }
  
  .checkbox {
    min-height: 54px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    box-sizing: border-box;
    margin-bottom: 12px;
    border-radius: 16px;
    background: var(--input-background);
    color: var(--text-primary);
    font-size: 14px;
    line-height: 1.35;
    font-family: 'Eastman-Regular', sans-serif;
  }
  
  .checkbox input {
    width: 20px;
    height: 20px;
    margin: 0;
    flex-shrink: 0;
    accent-color: #2173e5;
  }
  
  .field {
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
  }
  
  .field label {
    margin-bottom: 7px;
    color: var(--text-primary);
    font-size: 13px;
    font-family: 'Eastman-Medium', sans-serif;
  }
  
  .input-wrapper {
    min-height: 50px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    border: 1px solid #ddd;
    border-radius: 16px;
    padding: 0 14px;
    background: var(--input-background);
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  
  .input-wrapper.error {
    border-color: #e74c3c;
    box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.12);
  }
  
  [data-theme='dark'] .input-wrapper {
    background: #3a3a3a;
    border-color: #555;
  }
  
  [data-theme='dark'] .input-wrapper.error {
    border-color: #e27b6a;
    box-shadow: 0 0 0 3px rgba(226, 123, 106, 0.15);
  }
  
  .input-wrapper input {
    min-width: 0;
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 13px;
    color: var(--text-primary);
    font-family: 'Eastman-Regular', sans-serif;
  }
  
  .input-wrapper input::placeholder {
    color: var(--text-secondary);
  }
  
  .eye {
    width: 20px;
    height: 20px;
    opacity: 0.6;
    cursor: pointer;
    margin-left: 10px;
  }
  
  [data-theme='dark'] .eye {
    filter: invert(1);
  }
  
  .hint-error {
    margin: 6px 0 0;
    font-size: 12px;
    color: #d9534f;
  }
  
  [data-theme='dark'] .hint-error {
    color: #ff9a8f;
  }
  
  .primary-btn {
    width: 100%;
    height: 50px;
    margin-top: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: #2173e5;
    color: white;
    font-family: 'Eastman-Medium', sans-serif;
    font-size: 15px;
    border-radius: 999px;
    border: none;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(33, 115, 229, 0.22);
    transition: opacity 0.2s, background-color 0.2s, transform 0.05s;
  }
  
  .primary-btn:active {
    transform: translateY(1px);
  }
  
  .primary-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
  
  .btn-spinner {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    animation: spin 0.7s linear infinite;
  }
  
  .card.disabled {
    opacity: 0.58;
    pointer-events: none;
    user-select: none;
  }
  
  .disabled-badge {
    position: absolute;
    top: 18px;
    right: 18px;
    padding: 5px 10px;
    border-radius: 999px;
    background: rgba(33, 115, 229, 0.12);
    color: #2173e5;
    font-size: 11px;
    font-family: 'Eastman-Medium', sans-serif;
  }
  
  [data-theme='dark'] .disabled-badge {
    background: rgba(118, 180, 255, 0.16);
    color: #76b4ff;
  }
  
  .snackbar {
    position: fixed;
    left: 50%;
    bottom: calc(86px + env(safe-area-inset-bottom));
    transform: translateX(-50%);
    max-width: calc(100vw - 32px);
    background: #2173e5;
    color: white;
    padding: 10px 18px;
    border-radius: 14px;
    font-family: 'Eastman-Medium', sans-serif;
    font-size: 13px;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.22);
    z-index: 110;
  }
  
  .other-skeleton {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;
    border-radius: 24px;
    background: var(--sidebar-background);
  }
  
  .skeleton {
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.2s infinite;
    border-radius: 14px;
  }
  
  [data-theme='dark'] .skeleton {
    background: linear-gradient(90deg, #333 25%, #444 50%, #333 75%);
    background-size: 200% 100%;
  }
  
  .title-skeleton {
    width: 48%;
    height: 24px;
  }
  
  .title-skeleton.second {
    margin-top: 32px;
  }
  
  .row-skeleton {
    width: 100%;
    height: 54px;
    margin-top: 14px;
  }
  
  .field-skeleton {
    width: 100%;
    height: 50px;
    margin-top: 14px;
  }
  
  .button-skeleton {
    width: 100%;
    height: 50px;
    margin-top: 18px;
    border-radius: 999px;
  }
  
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.25s ease;
  }
  
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  @keyframes skeleton-shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
  
  @media (min-width: 768px) {
    .m-profile-page {
      max-width: 430px;
      margin: 0 auto;
      border-left: 1px solid var(--separator_home);
      border-right: 1px solid var(--separator_home);
    }
  }
  
  @media (max-width: 360px) {
    .m-profile-content {
      padding-left: 12px;
      padding-right: 12px;
    }
  
    .card,
    .other-skeleton {
      padding: 16px;
    }
  
    .card h2 {
      font-size: 17px;
    }
  }

  .theme-card {
  gap: 16px;
}

.theme-info h2 {
  margin-bottom: 6px;
}

.theme-info p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.4;
}

.theme-toggle {
  width: 100%;
  height: 54px;
  border: none;
  border-radius: 18px;
  padding: 0 14px;
  background: var(--input-background);
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Eastman-Medium', sans-serif;
  font-size: 14px;
  color: var(--text-primary);
}

.theme-icon {
  font-size: 18px;
}

.toggle-track {
  margin-left: auto;
  width: 52px;
  height: 28px;
  background: #2173e5;
  border-radius: 999px;
  position: relative;
  padding: 3px;
  box-sizing: border-box;
}

.toggle-thumb {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  transition: transform 0.25s ease, background 0.25s ease;
}

.toggle-thumb.light {
  transform: translateX(24px);
  background: #fdb813;
}

</style>
  